import { useState } from "react"
import { useQuery, useMutation } from "urql"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { StreamForm } from "./components/stream-form"
import { StreamsQuery, DeleteStreamMutation } from "@/lib/graphql/streaming"
import { ApplicationsQuery } from "@/lib/graphql/outbound"
import type { ResultOf } from "@/lib/graphql/types"

type StreamItem = ResultOf<typeof StreamsQuery>["streams"]["nodes"][number]

const LIMIT = 25

export function StreamsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)

  const applicationId = searchParams.get("applicationId") ?? undefined
  const statusFilter = searchParams.get("status") ?? ""
  const search = debouncedSearch || undefined
  const status = statusFilter || undefined
  const pagination = useCursorPagination(LIMIT, [applicationId, search, status])

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) params.set(key, value)
    else params.delete(key)
    setSearchParams(params, { replace: true })
  }

  const [applications] = useQuery({ query: ApplicationsQuery })
  const [result, reexecute] = useQuery({
    query: StreamsQuery,
    variables: {
      applicationId,
      search,
      status: status as "ACTIVE" | "PAUSED" | undefined,
      after: pagination.after,
      first: pagination.first,
    },
  })

  const [, deleteStream] = useMutation(DeleteStreamMutation)
  const [formOpen, setFormOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const columns: Column<StreamItem>[] = [
    {
      header: "Name",
      cell: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      header: "Application",
      cell: (row) => (
        <span className="text-xs text-muted-foreground">{row.application?.name ?? "-"}</span>
      ),
    },
    {
      header: "Status",
      cell: (row) => <StatusBadge status={row.status} />,
    },
    {
      header: "Retention",
      cell: (row) => (
        <span className="text-xs tabular-nums text-muted-foreground">{row.retentionDays}d</span>
      ),
    },
    {
      header: "Created",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
    {
      header: "",
      cell: (row) => (
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          aria-label="Delete"
          onClick={(e) => {
            e.stopPropagation()
            setDeleteId(row.id)
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      ),
      className: "w-12 text-right",
    },
  ]

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    const res = await deleteStream({ id: deleteId })
    setDeleting(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Stream deleted")
    setDeleteId(null)
    reexecute({ requestPolicy: "network-only" })
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Streams" description="Append-only event streams for applications">
        <Button onClick={() => setFormOpen(true)} size="sm" className="shadow-primary-glow transition-shadow">
          <Plus className="h-4 w-4 mr-1.5" />
          Create Stream
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Select
          value={applicationId ?? "all"}
          onValueChange={(v) => setFilter("applicationId", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-45">
            <SelectValue placeholder="All applications" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All applications</SelectItem>
            {applications.data?.applications.nodes.map((app) => (
              <SelectItem key={app.id} value={app.id}>{app.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={statusFilter || "all"}
          onValueChange={(v) => setFilter("status", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-35 text-sm">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="PAUSED">Paused</SelectItem>
          </SelectContent>
        </Select>

        <Input
          placeholder="Search streams..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
      </div>

      <DataTable
        columns={columns}
        data={result.data?.streams.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.streams.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No streams"
        emptyDescription="Create a stream to start capturing event data"
        onRowClick={(row) => navigate(`/streams/${row.id}`)}
      />

      <StreamForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
      />

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => { if (!open) setDeleteId(null) }}
        title="Delete Stream"
        description="This will permanently delete this stream and all its entries, consumers, and sinks."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        loading={deleting}
        destructive
      />
    </div>
  )
}
